import React, { useEffect, useMemo, useState } from 'react';
import { mediaStrings, moduleCommonStrings } from '../../content/hobbyModules';
import { ratingStrings } from '../../content/ratings';
import { validateReadingForm } from '../../validation/hobbies';
import useStepperFlow from '../../hooks/useStepperFlow';
import { includesCollectionName, normalizeCollectionOptions, resolveCollectionMode } from '../../utils/collectionOptions';
import { showFirstStepWithErrors } from '../../utils/formStepErrors';

const emptyBook = {
  id: null,
  title: '',
  subtitle: '',
  authors: '',
  description: '',
  coverUrl: '',
  publishedYear: '',
  pageCount: '',
  genres: '',
  shelf: 'Want to read',
  collection: 'My Books',
  format: 'Physical',
  favorite: false,
  rating: '',
  review: '',
  spoiler: false,
  progressType: 'Pages',
  currentPage: '',
  currentPercent: '',
  currentMinutes: '',
  startedDate: '',
  finishedDate: '',
  lastReadDate: '',
};
const stepErrorFields = {
  details: ['title', 'subtitle', 'authors', 'description', 'coverUrl', 'publishedYear', 'pageCount', 'genres'],
  library: ['shelf', 'collection', 'format', 'favorite', 'progressType', 'currentPage', 'currentPercent', 'currentMinutes', 'startedDate', 'finishedDate', 'lastReadDate'],
  review: ['rating', 'review', 'spoiler'],
};

/**
 * Creates initial form.
 *
 * @param {*} initialData - initial data value.
 * @returns {*} The derived operation result.
 */
function createInitialForm(initialData) {
  if (!initialData) return { ...emptyBook };

  return {
    ...emptyBook,
    ...initialData,
    authors: Array.isArray(initialData.authors) ? initialData.authors.join(', ') : initialData.authors || '',
    genres: Array.isArray(initialData.genres) ? initialData.genres.join(', ') : initialData.genres || '',
    pageCount: initialData.pageCount || '',
    currentPage: initialData.currentPage || '',
    currentPercent: initialData.currentPercent || '',
    currentMinutes: initialData.currentMinutes || '',
  };
}

/**
 * Renders the book star rating experience.
 *
 * @param {object} props - Component properties.
 * @param {*} props.value - Value to inspect or transform.
 * @param {*} props.onChange - Callback invoked when change occurs.
 * @returns {JSX.Element} The rendered interface.
 */
function BookStarRating({ value, onChange }) {
  const rating = Number(value || 0);
  const stars = Array.from({ length: 5 }, (_, index) => index + 1);

  /**
   * Retrieves star state.
   *
   * @param {*} star - star value.
   * @returns {*} The derived operation result.
   */
  const getStarState = (star) => {
    if (rating >= star) return 'full';
    if (rating >= star - 0.5) return 'half';
    return 'empty';
  };

  return (
    <div className="star-rating-wrap">
      <div className="star-rating" role="radiogroup" aria-label="Book rating">
        {stars.map((star) => (
          <span className={`star-button ${getStarState(star)}`} key={star}>
            <span className="star-icon" aria-hidden="true">★</span>
            <button
              type="button"
              className="star-hit star-hit-left"
              onClick={() => onChange(String(star - 0.5))}
              aria-pressed={rating === star - 0.5}
              aria-label={`${star - 0.5} out of 5 stars`}
            />
            <button
              type="button"
              className="star-hit star-hit-right"
              onClick={() => onChange(String(star))}
              aria-pressed={rating === star}
              aria-label={`${star} out of 5 stars`}
            />
          </span>
        ))}
      </div>
      <div className="star-rating-summary">
        <span>{rating ? ratingStrings.value(rating) : ratingStrings.notRated}</span>
        {rating > 0 && (
          <button type="button" className="btn-ghost compact-btn" onClick={() => onChange('')}>
            Clear
          </button>
        )}
      </div>
    </div>
  );
}

/** Collects book library, progress, and review data while preserving catalog metadata. */
export default function ReadingForm({ theme, onSave, onCancel, isSaving, initialData, onDirtyChange, collections = [], isEditing = false }) {
  const initialForm = useMemo(() => createInitialForm(initialData), [initialData]);
  const [form, setForm] = useState(initialForm);
  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const steps = useMemo(() => ['details', 'library', 'review'], []);
  const {
    activeSection,
    activeStepIndex,
    canGoBack,
    canGoNext,
    isStepSettled,
    goToSection,
    goToPreviousStep,
    goToNextStep,
    resetStepper,
  } = useStepperFlow(steps, 'details');
  const collectionOptions = useMemo(
    () => normalizeCollectionOptions(collections),
    [collections]
  );
  const isKnownCollection = includesCollectionName(collectionOptions, form.collection);
  const [collectionMode, setCollectionMode] = useState('existing');

  useEffect(() => {
    setForm(initialForm);
    resetStepper('details');
  }, [initialForm, resetStepper]);

  useEffect(() => {
    if (!collectionOptions.length) {
      setCollectionMode('new');
      return;
    }
    setCollectionMode(resolveCollectionMode({
      options: collectionOptions,
      value: initialForm.collection,
      defaultValue: emptyBook.collection,
    }));
  }, [collectionOptions, initialForm.collection]);

  useEffect(() => {
    if (collectionMode === 'existing' && collectionOptions.length && !includesCollectionName(collectionOptions, form.collection)) {
      updateField('collection', collectionOptions[0]);
    }
  }, [collectionMode, collectionOptions, form.collection]);

  useEffect(() => {
    onDirtyChange?.(JSON.stringify(form) !== JSON.stringify(initialForm));
  }, [form, initialForm, onDirtyChange]);

  /**
   * Updates field.
   *
   * @param {string} field - Field associated with the operation.
   * @param {*} value - Value to inspect or transform.
   * @returns {void} No value; the operation applies its documented side effects.
   */
  const updateField = (field, value) => {
    setForm((current) => ({ ...current, [field]: value }));
    setErrors((current) => ({ ...current, [field]: undefined }));
  };

  /**
   * Handles submit.
   *
   * @param {*} event - Browser or React event that triggered the action.
   * @returns {Promise<*>} A promise resolving to the completed operation result.
   */
  const handleSubmit = async (event) => {
    event.preventDefault();
    if (canGoNext) {
      goToNextStep();
      return;
    }
    if (!isStepSettled) return;
    if (collectionMode === 'new' && includesCollectionName(collectionOptions, form.collection)) {
      const nextErrors = { collection: mediaStrings.reading.form.errors.collectionDuplicate };
      setErrors((current) => ({ ...current, ...nextErrors }));
      showFirstStepWithErrors(nextErrors, stepErrorFields, 'library', goToSection);
      return;
    }
    const validation = validateReadingForm(form);
    setErrors(validation.fieldErrors);
    if (!validation.valid) {
      showFirstStepWithErrors(validation.fieldErrors, stepErrorFields, 'details', goToSection);
      return;
    }
    setIsSubmitting(true);
    try {
      await onSave(form);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form className="recipe-form reading-form" onSubmit={(event) => event.preventDefault()} noValidate>
      <div className="hobby-flow-stepper" role="tablist" aria-label="Book editor steps">
        {steps.map((section, index) => (
          <button
            key={section}
            type="button"
            className={activeSection === section ? 'active' : index < activeStepIndex ? 'complete' : ''}
            onClick={() => goToSection(section)}
            role="tab"
            aria-selected={activeSection === section}
          >
            <span>{index + 1}</span>
            {mediaStrings.reading.form.sections[section] || section}
          </button>
        ))}
      </div>

      {activeSection === 'details' && (
      <section className="recipe-form-section priority-section">
        <h3>{mediaStrings.reading.form.sections.book}</h3>
        <div className="form-grid two-columns">
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.title}</label>
            <input value={form.title} onChange={(event) => updateField('title', event.target.value)} aria-invalid={Boolean(errors.title)} disabled={isEditing} className={isEditing ? 'locked-field-input' : ''} />
            {errors.title && <p className="field-error">{errors.title}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.subtitle}</label>
            <input value={form.subtitle} onChange={(event) => updateField('subtitle', event.target.value)} />
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.authors}</label>
            <input value={form.authors} onChange={(event) => updateField('authors', event.target.value)} placeholder={mediaStrings.reading.form.placeholders.authors} />
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.publishedYear}</label>
            <input value={form.publishedYear} onChange={(event) => updateField('publishedYear', event.target.value)} inputMode="numeric" aria-invalid={Boolean(errors.publishedYear)} />
            {errors.publishedYear && <p className="field-error">{errors.publishedYear}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.pageCount}</label>
            <input value={form.pageCount} onChange={(event) => updateField('pageCount', event.target.value)} inputMode="numeric" aria-invalid={Boolean(errors.pageCount)} />
            {errors.pageCount && <p className="field-error">{errors.pageCount}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.genres}</label>
            <input value={form.genres} onChange={(event) => updateField('genres', event.target.value)} placeholder={mediaStrings.reading.form.placeholders.genres} />
          </div>
          <div className="form-group form-grid-full">
            <label>{mediaStrings.reading.form.fields.description}</label>
            <textarea rows="3" value={form.description} onChange={(event) => updateField('description', event.target.value)} />
          </div>
        </div>
      </section>
      )}

      {activeSection === 'library' && (
      <>
      <section className="recipe-form-section">
        <h3>{mediaStrings.reading.form.sections.library}</h3>
        <div className="form-grid three-columns">
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.shelf}</label>
            <select value={form.shelf} onChange={(event) => updateField('shelf', event.target.value)} aria-invalid={Boolean(errors.shelf)}>
              {mediaStrings.reading.form.options.shelves.map((shelf) => <option key={shelf}>{shelf}</option>)}
            </select>
            {errors.shelf && <p className="field-error">{errors.shelf}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.collection}</label>
            <div className="collection-picker">
              {collectionOptions.length > 0 && (
                <div className="collection-mode-toggle" role="group" aria-label={mediaStrings.reading.form.options.collectionMode}>
                  <button
                    type="button"
                    className={collectionMode === 'existing' ? 'active' : ''}
                    onClick={() => {
                      setCollectionMode('existing');
                      updateField('collection', isKnownCollection ? form.collection : collectionOptions[0]);
                    }}
                  >
                    {mediaStrings.movies.form.options.existing}
                  </button>
                  <button
                    type="button"
                    className={collectionMode === 'new' ? 'active' : ''}
                    onClick={() => {
                      setCollectionMode('new');
                      if (isKnownCollection) updateField('collection', '');
                    }}
                  >
                    {mediaStrings.movies.form.options.createNew}
                  </button>
                </div>
              )}
              {collectionMode === 'existing' && collectionOptions.length > 0 ? (
                <select value={isKnownCollection ? form.collection : collectionOptions[0]} onChange={(event) => updateField('collection', event.target.value)}>
                  {collectionOptions.map((collection) => <option key={collection} value={collection}>{collection}</option>)}
                </select>
              ) : (
                <input value={form.collection} onChange={(event) => updateField('collection', event.target.value)} placeholder={mediaStrings.reading.form.placeholders.collection} />
              )}
            </div>
            {errors.collection && <p className="field-error">{errors.collection}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.format}</label>
            <select value={form.format} onChange={(event) => updateField('format', event.target.value)} aria-invalid={Boolean(errors.format)}>
              {mediaStrings.reading.form.options.formats.map((format) => <option key={format}>{format}</option>)}
            </select>
            {errors.format && <p className="field-error">{errors.format}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.favorite}</label>
            <button
              type="button"
              className={`favorite-toggle inline-favorite-toggle ${form.favorite ? 'active' : ''}`}
              onClick={() => updateField('favorite', !form.favorite)}
              aria-pressed={form.favorite}
            >
              <span>{form.favorite ? mediaStrings.reading.form.favorite : mediaStrings.reading.form.favoriteAction}</span>
            </button>
          </div>
        </div>
      </section>

      <section className="recipe-form-section">
        <h3>{mediaStrings.reading.form.sections.progress}</h3>
        <div className="form-grid three-columns">
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.progressType}</label>
            <select value={form.progressType} onChange={(event) => updateField('progressType', event.target.value)} aria-invalid={Boolean(errors.progressType)}>
              {mediaStrings.reading.form.options.progressTypes.map((type) => <option key={type}>{type}</option>)}
            </select>
            {errors.progressType && <p className="field-error">{errors.progressType}</p>}
          </div>
          {form.progressType === 'Pages' && (
            <div className="form-group">
              <label>{mediaStrings.reading.form.fields.currentPage}</label>
              <input value={form.currentPage} onChange={(event) => updateField('currentPage', event.target.value)} inputMode="numeric" aria-invalid={Boolean(errors.currentPage)} />
              {errors.currentPage && <p className="field-error">{errors.currentPage}</p>}
            </div>
          )}
          {form.progressType === 'Percent' && (
            <div className="form-group">
              <label>{mediaStrings.reading.form.fields.currentPercent}</label>
              <input value={form.currentPercent} onChange={(event) => updateField('currentPercent', event.target.value)} inputMode="decimal" aria-invalid={Boolean(errors.currentPercent)} />
              {errors.currentPercent && <p className="field-error">{errors.currentPercent}</p>}
            </div>
          )}
          {form.progressType === 'Minutes' && (
            <div className="form-group">
              <label>{mediaStrings.reading.form.fields.currentMinutes}</label>
              <input value={form.currentMinutes} onChange={(event) => updateField('currentMinutes', event.target.value)} inputMode="numeric" aria-invalid={Boolean(errors.currentMinutes)} />
              {errors.currentMinutes && <p className="field-error">{errors.currentMinutes}</p>}
            </div>
          )}
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.started}</label>
            <input type="date" value={form.startedDate} onChange={(event) => updateField('startedDate', event.target.value)} aria-invalid={Boolean(errors.startedDate)} />
            {errors.startedDate && <p className="field-error">{errors.startedDate}</p>}
          </div>
          <div className="form-group">
            <label>{mediaStrings.reading.form.fields.finished}</label>
            <input type="date" value={form.finishedDate} onChange={(event) => updateField('finishedDate', event.target.value)} aria-invalid={Boolean(errors.finishedDate)} />
            {errors.finishedDate && <p className="field-error">{errors.finishedDate}</p>}
          </div>
        </div>
      </section>
      </>
      )}

      {activeSection === 'review' && (
      <section className="recipe-form-section">
        <h3>{mediaStrings.reading.form.sections.review}</h3>
        <div className="form-grid two-columns">
          <div className="form-group form-grid-full">
            <label>{mediaStrings.reading.form.fields.rating}</label>
            <BookStarRating value={form.rating} onChange={(value) => updateField('rating', value)} />
            {errors.rating && <p className="field-error">{errors.rating}</p>}
          </div>
          <div className="form-group form-grid-full">
            <label className="checkbox-row">
              <input type="checkbox" checked={form.spoiler} onChange={(event) => updateField('spoiler', event.target.checked)} />
              <span>{mediaStrings.reading.form.options.spoiler}</span>
            </label>
          </div>
          <div className="form-group form-grid-full">
            <label>{mediaStrings.reading.form.fields.review}</label>
            <textarea rows="5" value={form.review} onChange={(event) => updateField('review', event.target.value)} />
          </div>
        </div>
      </section>
      )}

      <div className="form-actions sticky-actions">
        <button type="button" className="btn-ghost" onClick={() => onCancel({ confirmDiscard: true })}>{moduleCommonStrings.actions.cancel}</button>
        <div className="stepper-actions">
          {canGoBack && (
            <button type="button" className="btn-secondary" onClick={goToPreviousStep}>
              {moduleCommonStrings.actions.back}
            </button>
          )}
          {canGoNext ? (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={goToNextStep}>
              {moduleCommonStrings.actions.next}
            </button>
          ) : (
            <button type="button" className="btn-primary" style={{ background: theme }} onClick={handleSubmit} disabled={isSaving || isSubmitting || !isStepSettled}>
              {isSaving || isSubmitting ? moduleCommonStrings.actions.saving : mediaStrings.reading.form.save}
            </button>
          )}
        </div>
      </div>
    </form>
  );
}
